import { useEffect, useRef, useState, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { API_CONFIG } from '@/lib/config';

// Socket token API endpoint
const SOCKET_TOKEN_API = '/api/socket-token';

// Socket event types
export interface Bid {
  currentHighestBid:number,
  totalBidCount:number,
  isHighestBidder:boolean,
  endDate:string
}

export interface Auction {
  id: string;
  status: 'Active' | 'Completed' | 'Cancelled';
  currentPrice: number;
  startDate: string;
  endDate: string;
  winner?: {
    userId: string;
    userName: string;
    amount: number;
  };
}

export interface AuctionSummary {
  auctionId: string;
  totalBids: number;
  participantCount: number;
  currentPrice: number;
  timeRemaining: string;
  status: string;
}

export interface CreateBidDto {
  auctionId: string;
  amount: number;
}

// Socket hook
export const useAuctionSocket = (auctionId: string, shouldConnect: boolean = false) => {
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(shouldConnect);

  // Socket event handlers - useRef kullanarak stale closure problemini çöz
  const onNewBidRef = useRef<((bid: Bid) => void) | null>(null);
  const onAuctionStatusUpdatedRef = useRef<((auction: Auction) => void) | null>(null);
  const onBidPlacedSuccessRef = useRef<((bid: Bid) => void) | null>(null);
  const onBidPlacedErrorRef = useRef<((error: string) => void) | null>(null);

  // Get token from API
  const getSocketToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch(SOCKET_TOKEN_API, {
        method: 'GET',
        credentials: 'include', // HTTP-only cookie'leri dahil et
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (data.token) {
        return data.token;
      }

      return null;
    } catch (error) {
      return null;
    }
  }, [auctionId]);

  // Socket event handlers - Sadece bir kez tanımla
  const setupEventHandlers = useCallback((connection: signalR.HubConnection) => {
    // Set up event handlers - Sadece bir kez tanımla
    connection.on('NewBidPlaced', (bid: Bid) => {
      // Sadece geçerli veri varsa handler'ı çağır
      if (bid && typeof bid === 'object' && bid.currentHighestBid) {
        setTimeout(() => {
          onNewBidRef.current?.(bid);
        }, 0);
      }
    });

    connection.on('AuctionStatusUpdated', (auction: Auction) => {
      // Sadece geçerli veri varsa handler'ı çağır
      if (auction && typeof auction === 'object' && auction.id) {
        setTimeout(() => {
          onAuctionStatusUpdatedRef.current?.(auction);
        }, 0);
      }
    });


    connection.on('BidPlacedSuccess', (bid: Bid) => {
      // Sadece geçerli veri varsa handler'ı çağır
      if (bid && typeof bid === 'object') {
        setTimeout(() => {
          onBidPlacedSuccessRef.current?.(bid);
        }, 0);
      }
    });

    connection.on('BidPlacedError', (error: string) => {
      // Sadece geçerli hata varsa handler'ı çağır
      if (error && typeof error === 'string' && error.trim() !== '' && error !== 'null') {
        setTimeout(() => {
          onBidPlacedErrorRef.current?.(error);
        }, 0);
      }
    });



    connection.on('AuctionJoined', (data: { [key: string]: unknown }) => {
      // Auction joined event
    });
  }, [auctionId]); // auctionId dependency eklendi

  // Try different transport types
  const tryConnectWithTransport = useCallback(async (transport: signalR.HttpTransportType, token: string): Promise<signalR.HubConnection | null> => {
    try {
      // WebSocket bağlantısı için config'den URL'i al
      const hubUrl = API_CONFIG.WEBSOCKET_HUB_URL;
      
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => token,
          transport: transport,
          skipNegotiation: transport === signalR.HttpTransportType.WebSockets
        })
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .configureLogging(signalR.LogLevel.None)
        .build();

      await connection.start();
      return connection;
    } catch {
      return null;
    }
  }, [auctionId]);

  // Connect to SignalR hub
  const connect = useCallback(async () => {
    // E-Devlet doğrulaması ve authentication kontrolü
    if (!shouldConnect) {
      setIsAuthenticated(false);
      return;
    }

    // API'den token al
    const token = await getSocketToken();
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    setIsAuthenticated(true);

    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);

      // Use only WebSockets transport
      const connection = await tryConnectWithTransport(signalR.HttpTransportType.WebSockets, token);
      
      if (!connection) {
        throw new Error('Failed to connect with WebSockets transport');
      }

      // Connection state change handlers
      connection.onclose((error) => {
        setIsConnected(false);
      });

      connection.onreconnecting((error) => {
        setIsConnected(false);
        setIsConnecting(true);
      });

      connection.onreconnected((connectionId) => {
        setIsConnected(true);
        setIsConnecting(false);
      });

      // Set up event handlers - Sadece bir kez
      setupEventHandlers(connection);
      
      connectionRef.current = connection;
      setIsConnected(true);
      setIsConnecting(false);

      // Join auction group
      await connection.invoke('JoinAuction', auctionId);


    } catch (err) {
      // Socket bağlantısı başarısız olsa da uygulama çalışmaya devam etsin
      setError(err instanceof Error ? err.message : 'Connection failed');
      
      setIsConnecting(false);
      setIsConnected(false);
    }
  }, [auctionId, setupEventHandlers, shouldConnect, tryConnectWithTransport, getSocketToken]);

  // Disconnect from SignalR hub
  const disconnect = useCallback(async () => {
    if (connectionRef.current) {
      try {
        // Leave auction group
        await connectionRef.current.invoke('LeaveAuction', auctionId);
        
        // Stop connection
        await connectionRef.current.stop();
        connectionRef.current = null;
        setIsConnected(false);
      } catch (err) {
        // Disconnect error
      }
    }
  }, [auctionId]);

  // Place bid
  const placeBid = useCallback(async (amount: number) => {
    // Socket bağlantısı kontrolü
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      throw new Error('Socket bağlantısı mevcut değil. Teklif göndermek için gerçek zamanlı bağlantı gereklidir.');
    }

    const bidData: CreateBidDto = {
      auctionId,
      amount
    };

    await connectionRef.current.invoke('PlaceBid', bidData);
  }, [auctionId]);



  // Auto-connect on mount - sadece authenticated ve E-Devlet doğrulamalı kullanıcılar için
  useEffect(() => {
    if (shouldConnect) {
      // Token kontrolü connect fonksiyonu içinde yapılacak
      connect();
    } else {
      // E-Devlet doğrulaması olmayan veya giriş yapmamış kullanıcılar için bağlantıyı kapat
      setIsAuthenticated(false);
      // Eğer bağlıysa bağlantıyı kes
      if (connectionRef.current) {
        disconnect();
      }
    }

    // Cleanup on unmount
    return () => {
      if (shouldConnect) {
        disconnect();
      }
    };
  }, [connect, disconnect, shouldConnect]);

  // Cleanup on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      disconnect();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [disconnect]);

  return {
    // Connection state
    isConnected,
    isConnecting,
    error,
    isAuthenticated,
    
    // Connection methods
    connect,
    disconnect,
    
    // Socket methods
    placeBid,
    
    // Event handlers - setter fonksiyonları artık ref'leri günceller
    setOnNewBid: (handler: ((bid: Bid) => void) | null) => { onNewBidRef.current = handler; },
    setOnAuctionStatusUpdated: (handler: ((auction: Auction) => void) | null) => { onAuctionStatusUpdatedRef.current = handler; },
    setOnBidPlacedSuccess: (handler: ((bid: Bid) => void) | null) => { onBidPlacedSuccessRef.current = handler; },
    setOnBidPlacedError: (handler: ((error: string) => void) | null) => { onBidPlacedErrorRef.current = handler; },
  };
};
